import React, {Component, ReactNode} from 'react';
import {goToRoute} from 'app/utils';
import {addGaEvent} from 'app/ga_methods';
import {useLocation} from 'react-router-dom';

interface FaqItemQuestionProps {
    title: string;
    text: string;
    name: string;
    scroll: boolean;
}

interface FaqItemQuestionState {
    opened: boolean;
}

const normalizePath = (p: string) => (p || '').split('?')[0].split('#')[0].replace(/\/+$/u, '');

const getSlugFromPath = (p: string): string => {
    const parts = normalizePath(p).split('/').filter(Boolean);
    return parts[parts.length - 1] || '';
};

type WithLocationProps = {locationPathname: string; locationKey: string};

const withLocation = <P extends object>(Wrapped: React.ComponentType<P & WithLocationProps>) => {
    const WithLocation = (props: P) => {
        const {pathname, key} = useLocation();
        return <Wrapped {...props} locationPathname={pathname} locationKey={String(key)}/>;
    };
    return WithLocation;
};

class FaqItemQuestion extends Component<FaqItemQuestionProps & WithLocationProps, FaqItemQuestionState> {
    faqItemOpener: React.RefObject<HTMLDivElement> = React.createRef();

    state: FaqItemQuestionState = {opened: false};

    deferScroll = (): void => {
        requestAnimationFrame(() => {
            this.faqItemOpener.current?.scrollIntoView();
        });
    };

    isOnCurrentFaq = (): boolean => getSlugFromPath(this.props.locationPathname) === this.props.name;

    openAnswer = (): void => {
        const willOpen = !this.state.opened;

        if (!this.isOnCurrentFaq() && willOpen) {
            goToRoute(`/faq/${this.props.name}`);
            return;
        }

        this.setState({opened: willOpen}, () => {
            addGaEvent('FaqItemQuestion', willOpen ? 'opened' : 'closed');
        });
    };

    isPageFaq = (): void => {
        if (!this.isOnCurrentFaq()) {
            return;
        }

        const afterOpen = () => {
            if (this.props.scroll) {
                this.deferScroll();
            }
        };

        if (this.state.opened) {
            afterOpen();
        } else {
            this.setState({opened: true}, afterOpen);
        }
    };

    componentDidMount(): void {
        if ('scrollRestoration' in window.history) {
            (window.history as History & {scrollRestoration?: 'auto' | 'manual' }).scrollRestoration = 'manual';
        }
        this.isPageFaq();
    }

    componentDidUpdate(prevProps: FaqItemQuestionProps & WithLocationProps): void {
        const pathnameChanged = this.props.locationPathname !== prevProps.locationPathname;
        const keyChanged = this.props.locationKey !== prevProps.locationKey;

        if (pathnameChanged || keyChanged) {
            this.isPageFaq();
        }
    }

    render(): ReactNode {
        const {title, text, name} = this.props;

        return (
            <div className="faq-item-question" ref={this.faqItemOpener}>
                <div className="title-question" onClick={this.openAnswer} id={name}>
                    <span>{title}</span>
                </div>
                <div className={`description-content-question${this.state.opened ? ' opened' : ''}`}>
                    <div className="description-question" dangerouslySetInnerHTML={{__html: text}} />
                </div>
            </div>
        );
    }
}

export default withLocation(FaqItemQuestion);
